import React, { useEffect, useState } from "react"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism"
import { Button, Form, FormInstance } from "antd"
import { CopyOutlined } from "@ant-design/icons"

import { notify } from "@src/app/components/notify"
import { WidgetCard } from "@src/pages/widgetModify/ui/WidgetCard"

type WidgetCodeProps = {
  code: string
}

type Props = {
  code: string
  form: FormInstance
}
const CopyButton = ({ code }: WidgetCodeProps) => {
  const copyText = (text: string): void => {
    navigator.clipboard.writeText(text)

    notify.success("Copied")
  }

  return (
    <Button
      disabled={false}
      icon={<CopyOutlined />}
      id="GENERATED_ID_WIDGETCODE_BUTTON_COPY_1"
      onClick={() => copyText(code)}
      size="middle"
      type="primary"
    >
      Copy
    </Button>
  )
}

export const WidgetCode = ({ code, form }: Props) => {
  const allValues = Form.useWatch([], form)

  const [codeUpdated, setCodeUpdated] = useState(code)

  useEffect(() => {
    const match = code.match(/src=['"]([^'"]+)['"]/)
    if (!match) return

    const originalSrc = match[1]
    if (!originalSrc) return

    const url = new URL(originalSrc)

    const keys = [
      "amount",
      "email",
      "currencies",
      "currenciesFiat",
      "currenciesAddresses",
      "currenciesFiatAddresses",
    ]

    const base = originalSrc

    const query = Object.entries(allValues ?? {})
      .filter(([k, v]) => {
        if (Array.isArray(v)) {
          return Boolean(v.length) && keys.includes(k)
        }

        if (typeof v === "object") {
          return Object.values(v as object).some(Boolean) && keys.includes(k)
        }

        return Boolean(v) && keys.includes(k)
      })
      .map(([k, v]) => {
        const isSimpleWay = [...keys.slice(0, 4)].includes(k)

        if (isSimpleWay) {
          return `${k}=${v}`
        }

        const isAddresses = ["currenciesAddresses"].includes(k)

        if (isAddresses) {
          const updated = Object.entries(v!).reduce(
            (acc, [k, v]) => {
              if (Boolean(v)) {
                acc[k] = v
              }

              return acc
            },
            {} as Record<string, any>,
          )

          return `addresses=${JSON.stringify(updated)}`
        }

        return null
      })
      .join("&")

    const finalUrl = query ? `${base}&${query}` : base

    const updatedIframe = code.replace(originalSrc, finalUrl as string)

    setCodeUpdated(formatIframe(updatedIframe))
  }, [allValues, code])

  const formatIframe = (iframeString: string) => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(iframeString, "text/html")

    const iframe = doc.querySelector("iframe")
    if (!iframe) return iframeString

    const attrs = iframe.attributes

    let result = "<iframe\n"

    for (let i = 0; i < attrs.length; i++) {
      const attr = attrs[i]
      result += `  ${attr?.name}="${attr?.value}"\n`
    }

    result += "></iframe>"

    return result
  }

  return (
    <WidgetCard headerAppend={<CopyButton code={code} />} title="Widget code">
      <SyntaxHighlighter
        language="html"
        style={oneDark}
        wrapLines
        wrapLongLines
      >
        {codeUpdated}
      </SyntaxHighlighter>
    </WidgetCard>
  )
}
